/**
 * Skeleton component for payroll page loading state
 * @module components/common/PayrollSkeleton
 */

import React from 'react'
import { Card } from '@/components/ui/Card'
import { SkeletonText, SkeletonRounded, SkeletonCircular } from './Skeleton'

/**
 * Payroll loading skeleton
 *
 * Displays skeleton placeholders matching the structure of the payroll page
 */
export const PayrollSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Period Selector - already visible, stats are loading */}

      {/* Stats Cards */}
      <div className="dashboard-grid lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <SkeletonText width={100} height={14} />
                <SkeletonText width={120} height={32} />
              </div>
              <SkeletonCircular width={48} height={48} />
            </div>
          </Card>
        ))}
      </div>

      {/* Cost Breakdown */}
      <Card className="p-6">
        <SkeletonText width={180} height={24} className="mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="text-center">
              <div className="p-4 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20">
                <SkeletonCircular width={16} height={16} className="mx-auto mb-2" />
                <SkeletonText width="80%" height={16} className="mx-auto" />
                <SkeletonText width="60%" height={12} className="mx-auto mt-1" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs skeleton */}
      <div className="flex gap-2 p-1 bg-brand-dark-lighter rounded-lg w-full">
        <SkeletonRounded width="50%" height={36} />
        <SkeletonRounded width="50%" height={36} />
      </div>

      {/* Filter bar */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <SkeletonRounded width="100%" height={40} className="flex-1" />
          <div className="flex gap-3">
            <SkeletonRounded width={180} height={40} />
            <SkeletonRounded width={150} height={40} />
          </div>
        </div>
      </Card>

      {/* Employee payroll cards */}
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, cardIndex) => (
          <Card key={cardIndex} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <SkeletonCircular width={48} height={48} />
                <div className="space-y-1">
                  <SkeletonText width={180} height={20} />
                  <div className="flex items-center gap-4">
                    <SkeletonText width={100} height={14} />
                    <SkeletonText width={100} height={14} />
                    <SkeletonText width={80} height={14} />
                  </div>
                </div>
              </div>
              <SkeletonRounded width={100} height={32} />
            </div>

            <div className="grid gap-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-brand-dark-lighter rounded-monumenta">
                  <div className="flex items-center gap-3">
                    <SkeletonCircular width={16} height={16} />
                    <div className="space-y-1">
                      <SkeletonText width={120} height={16} />
                      <SkeletonText width={180} height={14} />
                    </div>
                    <SkeletonRounded width={60} height={20} />
                  </div>
                  <div className="text-right space-y-1">
                    <SkeletonText width={100} height={16} />
                    <SkeletonRounded width={60} height={20} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}
